import { Link, useParams } from 'react-router-dom';
import { useEffect, useState } from 'react';
import { FaCalendar, FaUser, FaClock, FaTag, FaShareAlt, FaPlus, FaSearch } from 'react-icons/fa';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import { Helmet } from 'react-helmet-async';
// import axios from 'axios'; // Removed
import CreateBlogModal from './CreateBlogModal';

// Helper function to generate schema.org structured data
const generateBlogStructuredData = (posts) => ({
  "@context": "https://schema.org",
  "@type": "Blog",
  headline: "SkillBridgeAI Blog - Career Development & Skill Enhancement",
  description:
    "Discover expert insights, career advice, and the latest trends in professional development and skill enhancement.",
  publisher: {
    "@type": "Organization",
    name: "Gaplify",
    logo: {
      "@type": "ImageObject",
      url: "https://gaplify.com/logo.png",
    },
  },
  blogPost: posts.map((post) => ({
    "@type": "BlogPosting",
    headline: post.title,
    description: post.excerpt,
    datePublished: post.createdAt,
    author: { "@type": "Person", name: post.author },
    image: post.image,
    keywords: post.tags.join(', '),
  })),
});

import { getAllBlogs } from '../services/blogService';
// ... other imports

const Blog = () => {
  const { slug } = useParams();
  const [posts, setPosts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedPost, setSelectedPost] = useState(null);
  const [isCreateModalOpen, setIsCreateModalOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');

  // Fetch all blogs
  const fetchBlogs = async () => {
    try {
      const data = await getAllBlogs();
      if (data.success) {
        setPosts(data.data);
      }
    } catch (err) {
      console.error('Failed to fetch blogs:', err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchBlogs();
  }, []);

  // Set selected post when slug changes or posts are loaded
  useEffect(() => {
    if (slug && posts.length > 0) {
      const post = posts.find(p => p.slug === slug);
      setSelectedPost(post);
    } else {
        setSelectedPost(null);
    }
  }, [slug, posts]);

  // Handle new blog creation
  const handleBlogCreated = (newBlog) => {
    setPosts(prev => [newBlog, ...prev]);
  };

  // Filter posts based on search
  const filteredPosts = posts.filter(post => 
    post.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
    post.tags.some(tag => tag.toLowerCase().includes(searchTerm.toLowerCase()))
  );

  // Detailed view
  if (slug) {
    if (loading) {
       return (
        <div className="min-h-screen flex items-center justify-center bg-gray-50">
           <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-blue-600"></div>
        </div>
      );
    }

    if (!selectedPost) {
      return (
        <main className="container mx-auto px-4 py-24 text-center">
          <h2 className="text-2xl font-bold text-gray-900 mb-4">Article Not Found</h2>
          <Link to="/blog" className="text-blue-600 hover:underline">Return to Blog</Link>
        </main>
      );
    }

    const postStructuredData = generateBlogStructuredData([selectedPost]);

    return (
      <>
        <Helmet>
          <title>{selectedPost.title} | Gaplify Blog</title>
          <meta name="description" content={selectedPost.excerpt} />
          <meta property="og:title" content={`${selectedPost.title} | Gaplify Blog`} />
          <meta property="og:description" content={selectedPost.excerpt} />
          <meta property="og:type" content="article" />
          <meta property="og:image" content={selectedPost.image} />
          <script type="application/ld+json">{JSON.stringify(postStructuredData)}</script>
        </Helmet>

        <main className="min-h-screen bg-gray-50 pt-24 pb-12">
            <article className="container mx-auto px-4 max-w-4xl">
                <Link to="/blog" className="inline-flex items-center text-gray-600 hover:text-blue-600 mb-8 transition-colors">
                    ← Back to all posts
                </Link>

                <div className="bg-white rounded-3xl shadow-xl overflow-hidden animate-fade-in-up">
                    <div className="h-64 md:h-96 w-full relative">
                        <img 
                            src={selectedPost.image} 
                            alt={selectedPost.title} 
                            className="w-full h-full object-cover"
                        />
                        <div className="absolute inset-0 bg-gradient-to-t from-black/60 to-transparent"></div>
                        <div className="absolute bottom-0 left-0 p-8 text-white">
                             <div className="flex flex-wrap gap-2 mb-4">
                                {selectedPost.tags.map((tag, index) => (
                                    <span key={index} className="bg-white/20 backdrop-blur-md px-3 py-1 rounded-full text-sm font-medium border border-white/10">
                                        # {tag}
                                    </span>
                                ))}
                            </div>
                            <h1 className="text-3xl md:text-5xl font-bold mb-4 leading-tight">{selectedPost.title}</h1>
                            <div className="flex items-center space-x-6 text-sm md:text-base text-gray-200">
                                <span className="flex items-center">
                                    <FaCalendar className="mr-2" />
                                    {new Date(selectedPost.createdAt).toLocaleDateString('en-US', {
                                        year: 'numeric',
                                        month: 'long',
                                        day: 'numeric',
                                    })}
                                </span>
                                <span className="flex items-center">
                                    <FaUser className="mr-2" /> {selectedPost.author}
                                </span>
                                <span className="flex items-center">
                                    <FaClock className="mr-2" /> {selectedPost.readTime}
                                </span>
                            </div>
                        </div>
                    </div>

                    <div className="p-8 md:p-12">
                        <div className="prose prose-lg md:prose-xl max-w-none prose-headings:font-bold prose-headings:text-gray-900 prose-p:text-gray-600 prose-a:text-blue-600 prose-img:rounded-xl">
                            <ReactMarkdown remarkPlugins={[remarkGfm]}>
                                {selectedPost.content}
                            </ReactMarkdown>
                        </div>
                    </div>
                </div>
            </article>
        </main>
      </>
    );
  }

  const structuredData = generateBlogStructuredData(posts);

  return (
    <>
      <Helmet>
        <title>Career Development Blog | Gaplify</title>
        <meta
          name="description"
          content="Discover expert insights, career advice, and the latest trends in professional development and skill enhancement."
        />
        <script type="application/ld+json">{JSON.stringify(structuredData)}</script>
      </Helmet>

      <main className="min-h-screen bg-gray-50 pt-24 pb-12">
        <div className="container mx-auto px-4">
          {/* Header Section */}
          <header className="text-center mb-16 animate-fade-in-down">
             <div className="inline-flex items-center px-4 py-1.5 bg-blue-100 text-blue-700 rounded-full text-sm font-bold uppercase tracking-wider mb-6">
              Expert Insights
            </div>
            <h1 className="heading-primary mb-6 text-gray-900">
              Career Development <span className="text-transparent bg-clip-text bg-gradient-to-r from-blue-600 to-indigo-600">Blog</span>
            </h1>
            <p className="text-xl text-gray-600 max-w-2xl mx-auto mb-8">
              Unlock your potential with expert advice, industry trends, and actionable tips for your professional journey.
            </p>

            {/* Search and Action Bar */}
            <div className="max-w-4xl mx-auto flex flex-col md:flex-row gap-4 items-center justify-between bg-white p-2 rounded-2xl shadow-lg border border-gray-100">
                <div className="relative w-full md:flex-1">
                    <FaSearch className="absolute left-4 top-1/2 transform -translate-y-1/2 text-gray-400" />
                    <input 
                        type="text" 
                        placeholder="Search articles..." 
                        value={searchTerm}
                        onChange={(e) => setSearchTerm(e.target.value)}
                        className="w-full pl-12 pr-4 py-3 rounded-xl border-none focus:ring-0 text-gray-700 placeholder-gray-400"
                    />
                </div>
                <button 
                    onClick={() => setIsCreateModalOpen(true)}
                    className="w-full md:w-auto px-6 py-3 bg-gradient-to-r from-blue-600 to-indigo-600 text-white font-bold rounded-xl shadow-lg hover:shadow-xl hover:-translate-y-0.5 transition-all flex items-center justify-center gap-2"
                >
                    <FaPlus /> Write Article
                </button>
            </div>
          </header>

          {/* Blog Grid */}
          {loading ? (
             <div className="flex justify-center py-20">
                <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-blue-600"></div>
             </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
                {filteredPosts.length > 0 ? (
                    filteredPosts.map((post) => (
                        <article
                        key={post._id}
                        className="group bg-white rounded-[2rem] shadow-lg border border-gray-100 overflow-hidden flex flex-col h-full hover:shadow-2xl hover:border-blue-100 hover:-translate-y-1 transition-all duration-300"
                        >
                        {/* Image Container */}
                        <div className="h-56 relative overflow-hidden">
                            <img 
                                src={post.image} 
                                alt={post.title}
                                className="w-full h-full object-cover transform group-hover:scale-110 transition-transform duration-700"
                            />
                            <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-transparent to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300"></div>
                            
                             <div className="absolute bottom-4 left-4 flex flex-wrap gap-2">
                                {post.tags.slice(0, 2).map((tag, index) => (
                                    <span key={index} className="bg-white/90 backdrop-blur px-3 py-1 rounded-full text-xs font-bold text-gray-800 shadow-sm">
                                        {tag}
                                    </span>
                                ))}
                            </div>
                        </div>

                        {/* Content */}
                        <div className="p-6 flex flex-col flex-grow">
                            <div className="flex items-center space-x-4 mb-4 text-xs font-semibold text-gray-500 uppercase tracking-wide">
                                <span className="flex items-center text-blue-600">
                                    <FaCalendar className="mr-1.5" />
                                    {new Date(post.createdAt).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}
                                </span>
                                <span className="flex items-center">
                                    <FaClock className="mr-1.5" />
                                    {post.readTime}
                                </span>
                            </div>

                            <h2 className="text-xl font-bold text-gray-900 mb-3 leading-snug group-hover:text-blue-600 transition-colors line-clamp-2">
                                <Link to={`/blog/${post.slug}`}>
                                    {post.title}
                                </Link>
                            </h2>

                            <p className="text-gray-600 mb-6 line-clamp-3 flex-grow leading-relaxed">
                                {post.excerpt}
                            </p>

                            <div className="pt-6 border-t border-gray-100 flex items-center justify-between mt-auto">
                                <div className="flex items-center space-x-2">
                                    <div className="w-8 h-8 rounded-full bg-gradient-to-br from-blue-500 to-indigo-500 flex items-center justify-center text-white text-xs font-bold">
                                        {post.author.charAt(0)}
                                    </div>
                                    <span className="text-sm font-medium text-gray-700">{post.author}</span>
                                </div>
                                
                                <Link
                                    to={`/blog/${post.slug}`}
                                    className="text-blue-600 hover:text-indigo-600 font-bold text-sm flex items-center group/link transition-colors"
                                >
                                    Read Article <span className="ml-1 transform group-hover/link:translate-x-1 transition-transform">→</span>
                                </Link>
                            </div>
                        </div>
                        </article>
                    ))
                ) : (
                    <div className="col-span-full text-center py-20">
                        <div className="inline-block p-6 rounded-full bg-blue-50 text-blue-500 mb-4">
                            <FaSearch className="w-8 h-8"/>
                        </div>
                        <h3 className="text-xl font-bold text-gray-900 mb-2">No articles found</h3>
                        <p className="text-gray-500">Try adjusting your search terms or create a new article.</p>
                    </div>
                )}
            </div>
          )}

         

        </div>
      </main>

      <CreateBlogModal 
        isOpen={isCreateModalOpen} 
        onClose={() => setIsCreateModalOpen(false)}
        onBlogCreated={handleBlogCreated}
      />
    </>
  );
};

export default Blog;
